// Phase 11 — Bridge: legacy action worker UI shell, new proposal data source.
//
// The page keeps its SCSS shell (filter chips, card list, pagination), but the
// underlying data layer is now the Phase 8-F employee-scoped proposals API.
// Approve/Deny actions hit `/api/v2/employee/tara/proposals/:id/(approve|deny)`
// via the proposals client, and RBAC gates the action buttons.
//
// Scope rule (matches `Proposals.tsx` from the agency portal):
//   - Owner / manager default to "all shops" (no shop_slug filter).
//   - Team_lead / analyst / read_only default to the user's active shop slug.
//   - The shop selector lists accessible shops + an "All shops" entry where
//     allowed; legacy users with no employeeIdentity fall through to the
//     active_shop_slug stored in the user slice.
//
// Phase 12-A — Bridge resilience. The employee proposals endpoint requires
// `state.auth.agency.accessToken` (an `emp_` API key, minted at login by the
// Phase 11 bridge). Pre-bridge sessions persisted before the bridge shipped
// still carry only a seller JWT, so we early-return with a "refresh your
// session" CTA instead of firing a guaranteed-401 request.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import ActionCenterTabs from '../ActionCenterTabs';
import { HOME_ROUTE } from '../../../lib/constants/route';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import Popup from '../../../ui/popup/Popup';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import {
  listProposals,
  approveProposal,
  denyProposal,
  approveBatch,
  denyBatch,
  replicateProposal,
  CreativePreview,
  Proposal,
  BatchReviewResponse,
  ReplicationResult,
} from '../../../lib/api/proposals/proposals-api';
import {
  groupProposalsByBatch,
  collectCreativePreviews,
} from './batchGrouping';
import {
  isCreativeProposal,
  replicationTargetsFor,
  replicationTargetsForBatch,
  platformsToReplicate,
  replicationPlatformLabel,
  replicationOutcome,
  replicationLine,
} from './replication';
import BatchCard from './BatchCard';
import { setMessage } from '../../../store/reducers/appSlice';
import { useEmployeeRole, can } from '../../../lib/rbac';
import { tokenRevokeAtLogOutUser } from '../../../lib/api/logout-user';
import localStorageHardReset from '../../../lib/helper-functions/localStorageHardReset';
import type { RootState } from '../../../store/store';
import TeachingEmptyState from '../../../components/emptyState/TeachingEmptyState';
import { tickWithCircleSVG, crossSVG } from '../../../lib/allCustomSVGs';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import ProposalRow from './ProposalRow';
import ProposalDrawer from './ProposalDrawer';
import {
  Glyph,
  PROPOSAL_PLATFORM_OPTIONS,
  isAwaitingReview,
} from './proposalFormat';
import { listEmployees } from '../../../lib/api/identity/identity-api';
import '../actions.scss';
// Sentinel + badge rule live in ./pendingBadge so the rule is unit-testable.
import { pendingBadgeCount, ALL_SHOPS_SENTINEL } from './pendingBadge';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds
const PER_PAGE = 20;
// Phase 13 — per-client overview strip pulls counts client-side from a
// single listProposals() call (status=pending_review, no shop filter). Most
// queues run far below this ceiling; if a queue ever exceeds, the strip
// just under-counts — the filtered list below is unaffected.
const PER_SHOP_COUNTS_PAGE_SIZE = 500;

const ROLES_DEFAULTING_TO_ALL_SHOPS = new Set(['owner', 'manager']);

interface PerShopCount {
  name: string;
  count: number;
}

const ActionQueue = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useEmployeeRole();

  // Phase 4 — deep link: /actions/queue?batch={id} pre-filters the list to
  // that batch (the index accepts ?batch_id=) and auto-expands its card.
  // Follows the same useSearchParams mechanism as LogCenter / ControlCenter.
  const [searchParams, setSearchParams] = useSearchParams();
  const batchParam = searchParams.get('batch') || '';

  // Pull active shop and accessible shops from Redux. The active shop slug is
  // the legacy seller-side "currently selected" context — useful as a default
  // when employeeIdentity is missing (auto-bridged seller logins, etc).
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const accessibleShops = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.accessible_shops || []
  );
  // Phase 13 — owners auto-bridged from a seller login may have an empty
  // accessible_shops list but still own multiple brands via `all_shops`.
  // Used as a fallback source for the per-client strip so owners see every
  // brand even when no pending proposals exist for some of them.
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  // Phase 12-A — the employee proposals endpoint authenticates with the
  // Phase 11 bridge `emp_` key in this slice. Empty string means the user
  // logged in before the bridge was deployed (or via a flow that doesn't
  // mint the key) — we render a session-refresh CTA instead of firing a
  // 401-guaranteed request.
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken || ''
  );
  const hasAgencyToken = Boolean(agencyToken);

  const defaultsToAllShops = role
    ? ROLES_DEFAULTING_TO_ALL_SHOPS.has(role)
    : true;

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('pending_review');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [shopFilter, setShopFilter] = useState<string>(
    defaultsToAllShops ? ALL_SHOPS_SENTINEL : activeShopSlug || ''
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Phase 13 — per-client overview strip. Map of shop_slug → { name, count }
  // derived from the unfiltered pending_review list. `totalPending` is the
  // sum (cheaper than re-iterating in the render).
  const [perShopCounts, setPerShopCounts] = useState<Map<string, PerShopCount>>(
    () => new Map()
  );
  const [totalPending, setTotalPending] = useState(0);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Confirm-popup state. Replaces the native window.confirm() blocking dialogs
  // with the shared `Popup` pattern used across Control Center / Team. The
  // pending action's args are captured until the user confirms or cancels.
  // Phase 4 adds the batch-level variants (count shown in the dialog; deny
  // carries optional notes that flow back to the proposer as review feedback).
  const [pendingConfirm, setPendingConfirm] = useState<
    | { kind: 'approve'; id: string; notes?: string; proposal?: Proposal }
    | { kind: 'deny'; id: string; reason: string }
    | { kind: 'batchApprove'; batchId: string; count: number }
    | { kind: 'batchDeny'; batchId: string; count: number }
    | null
  >(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  // Notes typed into the batch confirm dialog (optional on both verbs).
  const [batchNotes, setBatchNotes] = useState('');
  // Phase 4 — "also launch on other platforms". Platforms ticked in the
  // approve confirm dialog (single or batch); reset every time a confirm is
  // staged. Only creative proposals surface the checkboxes.
  const [replicateTargets, setReplicateTargets] = useState<Set<string>>(
    () => new Set()
  );
  // Per-platform replicate results after a SINGLE approve (batch results ride
  // along inside `batchResult.replication` instead).
  const [replicationResult, setReplicationResult] = useState<{
    results: ReplicationResult[];
    error?: string;
  } | null>(null);

  // Phase 4 — expanded batch cards + per-item results of the last batch verb.
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(
    () => new Set()
  );
  const [batchResult, setBatchResult] = useState<{
    kind: 'approve' | 'deny';
    response: BatchReviewResponse;
    // id → display label, captured when the verb fires — the refresh that
    // follows may drop the items out of the current filter view.
    labels: Record<string, string>;
    // Phase 4 — per-item replicate outcomes when "also launch on" platforms
    // were ticked on a batch approve. Keyed by proposal id.
    replication?: Record<
      string,
      { results: ReplicationResult[]; error?: string }
    >;
  } | null>(null);

  // Detail-drawer state. A row click opens it for review; a row's quick-deny
  // opens it straight into deny mode (denials require a reason).
  const [activeProposal, setActiveProposal] = useState<Proposal | null>(null);
  const [drawerDenyMode, setDrawerDenyMode] = useState(false);
  const [drawerSubmitting, setDrawerSubmitting] = useState(false);

  // UUID -> display name for requester attribution. Best-effort; see effect.
  const [employeeNames, setEmployeeNames] = useState<Map<string, string>>(
    () => new Map()
  );

  const canApprove = can(role, 'proposals.approve');
  const canDeny = can(role, 'proposals.deny');
  const canAct = canApprove || canDeny;

  const isActionable = isAwaitingReview;

  const requesterName = (proposal: Proposal): string | undefined =>
    proposal.requested_by_employee_id
      ? employeeNames.get(proposal.requested_by_employee_id)
      : undefined;

  // Logout + redirect to /login. Mirrors `logoutHandler` in `App.tsx` so a
  // legacy seller-only session can re-auth through the Phase 11 bridge and
  // pick up an `emp_` key on the next login.
  const handleSessionRefresh = useCallback(() => {
    tokenRevokeAtLogOutUser();
    localStorageHardReset();
    navigate('/login', { replace: true });
  }, [navigate]);

  const statusTabs = [
    { label: t('All'), value: '' },
    { label: t('PendingReview'), value: 'pending_review' },
    { label: t('Approved'), value: 'approved' },
    { label: t('MyProposalsStatusExecuting'), value: 'executing' },
  ];

  // Compose the shop selector options. Always show the current shop list the
  // user has access to; owners (no accessible_shops list — they see everything)
  // still get the active shop as a quick filter when it exists.
  const shopOptions: Array<{ slug: string; name: string }> = [];
  if (accessibleShops.length > 0) {
    accessibleShops.forEach((s) => {
      const slug = s.slug ?? s.brand_slug;
      const name = s.name ?? s.brand_name ?? slug ?? '';
      if (slug) shopOptions.push({ slug, name });
    });
  } else if (activeShopSlug) {
    shopOptions.push({ slug: activeShopSlug, name: activeShopSlug });
  }

  const canSeeAllShops = defaultsToAllShops || shopOptions.length === 0;

  // Phase 14 — assemble the ordered per-client brand list that populates the
  // brand dropdown (formerly the horizontal strip). Source order:
  //   1. accessibleShops (the same source the dropdown uses)
  //   2. activeShopSlug fallback (matches dropdown behaviour)
  //   3. user.profile.all_shops (covers owners with empty accessible_shops)
  //   4. any shop discovered in perShopCounts that isn't already covered
  // Zero-pending shops still appear so the owner sees the full book; only the
  // "All clients" option is conditional on `canSeeAllShops`.
  const stripCards = useMemo(() => {
    const seen = new Set<string>();
    const cards: Array<{ slug: string; name: string; count: number }> = [];

    const push = (slug: string | undefined, name: string | undefined) => {
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      const fromCounts = perShopCounts.get(slug);
      cards.push({
        slug,
        name: name || fromCounts?.name || slug,
        count: fromCounts?.count ?? 0,
      });
    };

    if (accessibleShops.length > 0) {
      accessibleShops.forEach((s) => {
        push(s.slug ?? s.brand_slug, s.name ?? s.brand_name);
      });
    } else if (activeShopSlug) {
      push(activeShopSlug, activeShopSlug);
    }

    // SECURITY: do NOT fall back to `user.profile.all_shops` here. That list
    // comes from the seller-init/seller-JWT surface, which is not scoped to the
    // logged-in employee's org and can carry another account's brands (it also
    // persists across logins) — surfacing it here leaked other tenants' brands
    // into this dropdown. `accessible_shops` from /api/v1/me is the scoped,
    // authoritative source (owners get all their org's shops there too).
    // Tail: any extra shop that surfaced in the pending proposals but wasn't
    // in either list above (defensive — shouldn't normally happen).
    perShopCounts.forEach((info, slug) => push(slug, info.name));

    return cards;
  }, [
    accessibleShops,
    activeShopSlug,
    allShops,
    perShopCounts,
    defaultsToAllShops,
  ]);

  const fetchProposals = useCallback(async () => {
    // Phase 12-A — bail before hitting the wire when the agency Bearer is
    // empty. The endpoint's `employee_auth` middleware returns 401, but we'd
    // rather show the in-page CTA than a generic error toast.
    if (!agencyToken) {
      setLoading(false);
      return;
    }
    try {
      const result = await listProposals({
        // A ?batch= deep link narrows to that batch regardless of status —
        // the linked batch may already be partially reviewed.
        status: batchParam ? undefined : statusFilter || undefined,
        platform: platformFilter || undefined,
        shop_slug:
          shopFilter && shopFilter !== ALL_SHOPS_SENTINEL
            ? shopFilter
            : undefined,
        batch_id: batchParam || undefined,
        page: currentPage,
        per_page: PER_PAGE,
      });

      setProposals(result.data);
      setTotalPages(result.meta.last_page);
      setFetchError('');
    } catch (error: any) {
      console.error('[ActionQueue] fetch failed:', error);
      const status: number | undefined = error?.status;
      const friendly =
        status === 401 || status === 403
          ? t('FailedToLoadActionProposals')
          : error?.message || t('FailedToLoadActionProposals');
      dispatch(
        setMessage({
          message: friendly,
          messageType: 'error',
        })
      );
      setFetchError(friendly);
      setProposals([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    agencyToken,
    statusFilter,
    platformFilter,
    shopFilter,
    batchParam,
    currentPage,
    dispatch,
    t,
  ]);

  // Phase 13 — per-client strip data. Pulls ALL pending_review proposals
  // visible to the employee (no shop filter), then groups client-side. The
  // backend already scopes by accessible_shops, so this lists exactly what
  // the employee can act on. Failure is silent: the strip just stays empty
  // and the rest of the queue continues to work.
  const fetchPerShopCounts = useCallback(async () => {
    if (!agencyToken) return;
    try {
      const result = await listProposals({
        status: 'pending_review',
        page: 1,
        per_page: PER_SHOP_COUNTS_PAGE_SIZE,
      });
      const grouped = new Map<string, PerShopCount>();
      for (const p of result.data) {
        const slug = p.shop?.slug;
        if (!slug) continue;
        const existing = grouped.get(slug);
        if (existing) {
          existing.count += 1;
        } else {
          grouped.set(slug, {
            name: p.shop?.name || slug,
            count: 1,
          });
        }
      }
      setPerShopCounts(grouped);
      setTotalPending(result.meta?.total ?? result.data.length);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.warn('[ActionQueue] per-shop counts fetch failed', error);
    }
  }, [agencyToken]);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
  }, [statusFilter, platformFilter, shopFilter, batchParam]);

  // Phase 4 — ?batch= deep link: auto-expand the linked batch card and scroll
  // it into view once the filtered list has rendered. The ref makes the scroll
  // one-shot per batch id — without it the 30s auto-refresh (new `proposals`
  // identity) would yank the viewport back every cycle.
  const scrolledBatchRef = useRef<string>('');
  useEffect(() => {
    if (!batchParam || loading) return;
    setExpandedBatches((prev) => {
      if (prev.has(batchParam)) return prev;
      const next = new Set(prev);
      next.add(batchParam);
      return next;
    });
    if (scrolledBatchRef.current === batchParam) return;
    const el = document.getElementById(`aq-batch-${batchParam}`);
    if (el) {
      scrolledBatchRef.current = batchParam;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [batchParam, loading, proposals]);

  const clearBatchFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('batch');
    setSearchParams(next, { replace: true });
  };

  const toggleBatch = (batchId: string) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Phase 13 — initial + auto-refresh fetch for the per-shop strip. Runs on
  // the same 30s cadence as the main list so the counts stay roughly in
  // sync. Independent of the active filters — the strip is the unfiltered
  // overview.
  useEffect(() => {
    if (!hasAgencyToken) return;
    fetchPerShopCounts();
    const timer = setInterval(() => {
      fetchPerShopCounts();
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchPerShopCounts, agencyToken]);

  // Auto-refresh — skip entirely when we have no agency token (legacy seller
  // session pre-Phase-11-bridge) so we don't spam 401s every 30s.
  useEffect(() => {
    if (!hasAgencyToken) return;
    refreshTimer.current = setInterval(() => {
      fetchProposals();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
      }
    };
  }, [fetchProposals, agencyToken]);

  // Resolve requester UUIDs to display names for row/drawer attribution.
  // Best-effort and fail-silent: the roster endpoint may 403 for non-owners,
  // in which case the UI degrades to a neutral person glyph.
  useEffect(() => {
    if (!hasAgencyToken) return;
    let cancelled = false;
    listEmployees()
      .then((emps) => {
        if (cancelled) return;
        const next = new Map<string, string>();
        emps.forEach((e) => {
          if (e.id) next.set(e.id, e.name || e.email || e.id);
        });
        setEmployeeNames(next);
      })
      .catch(() => {
        /* non-owners can't list employees — rows fall back to a glyph */
      });
    return () => {
      cancelled = true;
    };
  }, [agencyToken]);

  // Card actions now stage a confirmation in the shared Popup instead of firing
  // a native window.confirm(). The deny-reason length guard runs up-front so the
  // user gets immediate inline feedback before any modal opens.
  const handleApprove = (id: string, notes?: string) => {
    // Capture the full proposal so the confirm dialog can detect a creative
    // launch (replicate checkboxes + preview thumbnail at the decision point).
    const proposal = proposals.find((p) => p.id === id);
    setReplicateTargets(new Set());
    setPendingConfirm({ kind: 'approve', id, notes, proposal });
  };

  // Phase 4 — batch verbs stage the same confirm Popup, carrying the count so
  // the dialog can say how many proposals are affected. Notes reset per open.
  const handleBatchApprove = (batchId: string, count: number) => {
    setBatchNotes('');
    setReplicateTargets(new Set());
    setPendingConfirm({ kind: 'batchApprove', batchId, count });
  };

  const handleBatchDeny = (batchId: string, count: number) => {
    setBatchNotes('');
    setPendingConfirm({ kind: 'batchDeny', batchId, count });
  };

  const toggleReplicateTarget = (key: string) => {
    setReplicateTargets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // The batch confirm dialog's creative items: actionable proposals of the
  // staged batch that qualify for replication. Recomputed per render — cheap
  // (one filter over the current page) and always in sync with the list.
  const batchCreativeItems = (batchId: string): Proposal[] =>
    proposals.filter(
      (p) =>
        p.batch_id === batchId &&
        isActionable(p.status) &&
        isCreativeProposal(p)
    );

  const executeConfirm = async () => {
    if (!pendingConfirm) return;
    setConfirmSubmitting(true);
    try {
      if (pendingConfirm.kind === 'approve') {
        await approveProposal(pendingConfirm.id, pendingConfirm.notes);
        dispatch(
          setMessage({
            message: t('ActionApprovedSuccessfully'),
            messageType: 'success',
          })
        );
        // Phase 4 — replicate AFTER approve succeeded (the backend rejects
        // replication of non-approved proposals). A replicate failure never
        // masks the successful approval: it surfaces in the results popup.
        const prop = pendingConfirm.proposal;
        if (prop && isCreativeProposal(prop)) {
          const platforms = platformsToReplicate(prop, replicateTargets);
          if (platforms.length > 0) {
            try {
              const rep = await replicateProposal(pendingConfirm.id, platforms);
              setReplicationResult({ results: rep.results });
            } catch (repErr: any) {
              console.error('[ActionQueue] replicate failed', repErr);
              setReplicationResult({
                results: [],
                error: repErr?.message || t('ReplicationRequestFailed'),
              });
            }
          }
        }
      } else if (pendingConfirm.kind === 'deny') {
        await denyProposal(pendingConfirm.id, pendingConfirm.reason);
        dispatch(
          setMessage({
            message: t('ActionDeniedSuccessfully'),
            messageType: 'success',
          })
        );
      } else {
        // Batch verbs return per-item results; surface them in a dedicated
        // results popup instead of a single toast so failures are visible.
        const isApprove = pendingConfirm.kind === 'batchApprove';
        const labels: Record<string, string> = {};
        proposals.forEach((p) => {
          labels[p.id] = p.entity_name || p.entity_id;
        });
        const response = isApprove
          ? await approveBatch(pendingConfirm.batchId, batchNotes || undefined)
          : await denyBatch(pendingConfirm.batchId, batchNotes || undefined);
        // Phase 4 — after a batch approve with ticked platforms, replicate
        // each approved CREATIVE item sequentially. Per-item failures are
        // tolerated and reported in the results popup; non-creative items are
        // skipped client-side (the endpoint would reject them anyway).
        let replication:
          | Record<string, { results: ReplicationResult[]; error?: string }>
          | undefined;
        if (isApprove && replicateTargets.size > 0) {
          const approvedIds = new Set(
            response.results
              .filter((r) => r.status === 'approved')
              .map((r) => r.id)
          );
          const creativeItems = proposals.filter(
            (p) =>
              p.batch_id === pendingConfirm.batchId &&
              approvedIds.has(p.id) &&
              isCreativeProposal(p)
          );
          for (const item of creativeItems) {
            const platforms = platformsToReplicate(item, replicateTargets);
            if (platforms.length === 0) continue;
            if (!replication) replication = {};
            try {
              // eslint-disable-next-line no-await-in-loop
              const rep = await replicateProposal(
                item.id,
                platforms,
                batchNotes || undefined
              );
              replication[item.id] = { results: rep.results };
            } catch (repErr: any) {
              console.error(
                `[ActionQueue] replicate failed for ${item.id}`,
                repErr
              );
              replication[item.id] = {
                results: [],
                error: repErr?.message || t('ReplicationRequestFailed'),
              };
            }
          }
        }
        setBatchResult({
          kind: isApprove ? 'approve' : 'deny',
          response,
          labels,
          replication,
        });
      }
      setPendingConfirm(null);
      fetchProposals();
      fetchPerShopCounts();
    } catch (err: any) {
      console.error(`[ActionQueue] ${pendingConfirm.kind} failed`, err);
      const isApproveKind =
        pendingConfirm.kind === 'approve' ||
        pendingConfirm.kind === 'batchApprove';
      const fallback = isApproveKind
        ? t('FailedToApproveAction')
        : t('FailedToDenyAction');
      const msg =
        err?.status === 409
          ? t('ProposalAlreadyProcessed')
          : err?.message || fallback;
      dispatch(setMessage({ message: msg, messageType: 'error' }));
      setPendingConfirm(null);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  // Drawer open paths. A plain row click opens it for review; the row's
  // quick-deny opens it straight into deny mode (a denial needs a reason, so
  // it can't fire from a single click the way approve can).
  const openDrawer = (proposal: Proposal) => {
    setDrawerDenyMode(false);
    setActiveProposal(proposal);
  };

  const openDenyDrawer = (proposal: Proposal) => {
    setDrawerDenyMode(true);
    setActiveProposal(proposal);
  };

  // Drawer actions execute directly — the drawer IS the review surface, and a
  // denial's required reason is its own confirmation. (Row quick-approve still
  // routes through the confirm Popup below.)
  const handleDrawerApprove = async (
    proposal: Proposal,
    notes?: string,
    replicatePlatforms?: string[]
  ) => {
    setDrawerSubmitting(true);
    try {
      await approveProposal(proposal.id, notes);
      dispatch(
        setMessage({
          message: t('ActionApprovedSuccessfully'),
          messageType: 'success',
        })
      );
      // Phase 4 — drawer-side "also launch on" ticks. Same rules as the
      // confirm-dialog path: replicate only AFTER approve succeeded, results
      // reuse the single-approve popup, and a replicate failure never masks
      // the successful approval.
      if (
        replicatePlatforms &&
        replicatePlatforms.length > 0 &&
        isCreativeProposal(proposal)
      ) {
        try {
          const rep = await replicateProposal(proposal.id, replicatePlatforms);
          setReplicationResult({ results: rep.results });
        } catch (repErr: any) {
          console.error('[ActionQueue] drawer replicate failed', repErr);
          setReplicationResult({
            results: [],
            error: repErr?.message || t('ReplicationRequestFailed'),
          });
        }
      }
      setActiveProposal(null);
      fetchProposals();
      fetchPerShopCounts();
    } catch (err: any) {
      const msg =
        err?.status === 409
          ? t('ProposalAlreadyProcessed')
          : err?.message || t('FailedToApproveAction');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setDrawerSubmitting(false);
    }
  };

  const handleDrawerDeny = async (proposal: Proposal, reason: string) => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      dispatch(
        setMessage({ message: t('DenyReasonRequired'), messageType: 'error' })
      );
      return;
    }
    setDrawerSubmitting(true);
    try {
      await denyProposal(proposal.id, trimmed);
      dispatch(
        setMessage({
          message: t('ActionDeniedSuccessfully'),
          messageType: 'success',
        })
      );
      setActiveProposal(null);
      fetchProposals();
      fetchPerShopCounts();
    } catch (err: any) {
      const msg =
        err?.status === 409
          ? t('ProposalAlreadyProcessed')
          : err?.message || t('FailedToDenyAction');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setDrawerSubmitting(false);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className='action-pagination'>
        <button
          className='action-pagination__btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          {t('Previous')}
        </button>
        {pages.map((page) => (
          <button
            key={page}
            className={`action-pagination__btn ${
              page === currentPage ? 'action-pagination__btn--active' : ''
            }`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          className='action-pagination__btn'
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(totalPages, prev + 1))
          }
        >
          {t('Next')}
        </button>
      </div>
    );
  };

  // Phase 12-A — no agency Bearer means a pre-bridge session. Don't render
  // the filters / list at all; show a single CTA that drops them back at
  // /login so the next sign-in mints the `emp_` key.
  if (!hasAgencyToken) {
    return (
      <SubPageInnerContentHolder
        title={t('ActionCenter')}
        backBtnLink={HOME_ROUTE}
      >
        <div className='action-empty-state'>
          <p className='action-empty-state__text'>
            {t('ActionQueueSessionRefreshRequired')}
          </p>
          <button
            type='button'
            className='action-pagination__btn action-pagination__btn--active'
            style={{ marginTop: '1rem' }}
            onClick={handleSessionRefresh}
          >
            {t('LogOutAndReLogin')}
          </button>
        </div>
      </SubPageInnerContentHolder>
    );
  }

  // Phase 14 — the per-client horizontal-scroll strip was replaced by a clean
  // brand dropdown (below). `stripCards` still assembles the full, ordered
  // brand list (with pending counts) — now used to populate the dropdown
  // options. Owner/manager get "All clients" + every accessible shop;
  // team_lead/analyst get their shops.
  const brandOptions = stripCards;

  // The pending badge sits directly beside the brand selector, so it must count
  // the SELECTED brand — not every brand. It previously always rendered
  // `totalPending` (the all-brands sum), so narrowing the filter to a brand with
  // no pending work still read "8 Pending" right next to that brand's name,
  // which reads as "this client has 8 items awaiting you". Found on dev
  // 2026-08-20: filter set to boots-uk.com, list correctly empty, badge still
  // claimed 8. The per-brand counts already exist on the same options the
  // dropdown renders, so this is purely a matter of reading the right one.
  const badgePending = pendingBadgeCount(
    shopFilter,
    totalPending,
    brandOptions
  );

  // Phase 4 — collapse proposals sharing a batch_id into one card. Ungrouped
  // proposals pass through as singles and render exactly as before.
  const queueEntries = groupProposalsByBatch(proposals);

  const batchResultLabel = (id: string): string =>
    batchResult?.labels[id] || id;

  // Shared per-platform result list — used by both the single-approve popup
  // and the per-item sub-lines inside the batch results popup.
  const renderReplicationList = (rep: {
    results: ReplicationResult[];
    error?: string;
  }) => (
    <ul className='aq-represult__list'>
      {rep.error && (
        <li className='aq-represult__row aq-represult__row--skipped'>
          <span className='aq-represult__icon'>
            <Glyph name='x' size={13} stroke={2.1} />
          </span>
          <span className='aq-represult__text'>{rep.error}</span>
        </li>
      )}
      {rep.results.map((r) => (
        <li
          key={r.platform}
          className={`aq-represult__row aq-represult__row--${
            replicationOutcome(r.status).tone
          }`}
        >
          <span className='aq-represult__icon'>
            <Glyph
              name={replicationOutcome(r.status).glyph}
              size={13}
              stroke={2.1}
            />
          </span>
          <span className='aq-represult__platform'>
            {replicationPlatformLabel(r.platform)}
          </span>
          <span className='aq-represult__text'>{replicationLine(r, t)}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <SubPageInnerContentHolder
      title={t('ActionCenter')}
      backBtnLink={HOME_ROUTE}
    >
      <ActionCenterTabs />
      {/* Filters */}
      <div className='action-queue-filters'>
        <div className='action-queue-filters__status-group'>
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={`action-queue-filters__filter-btn ${
                statusFilter === tab.value
                  ? 'action-queue-filters__filter-btn--active'
                  : ''
              }`}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Brand dropdown — replaces the per-client strip. Each option carries
            its pending count as a trailing badge; the trigger shows the total
            pending across all clients. Only rendered when the user has more
            than one option or can opt out of an active-shop scope. */}
        {(brandOptions.length > 1 || canSeeAllShops) && (
          <div className='action-queue-filters__brand'>
            <label
              className='action-queue-filters__brand-label'
              htmlFor='action-queue-brand-select'
            >
              {t('Brand')}:
            </label>
            <select
              id='action-queue-brand-select'
              className='action-queue-filters__platform-select'
              value={shopFilter}
              onChange={(e) => setShopFilter(e.target.value)}
              aria-label={t('ShopFilter')}
            >
              {canSeeAllShops && (
                <option value={ALL_SHOPS_SENTINEL}>
                  {t('AllClients')} ({totalPending})
                </option>
              )}
              {brandOptions.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.name}
                  {b.count > 0 ? ` (${b.count})` : ''}
                </option>
              ))}
            </select>
            {badgePending > 0 && (
              <span
                className='action-queue-filters__pending-badge'
                title={t('Pending')}
              >
                {badgePending} {t('Pending')}
              </span>
            )}
          </div>
        )}

        <select
          className='action-queue-filters__platform-select'
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          aria-label={t('PlatformFilter')}
        >
          <option value=''>{t('AllPlatforms')}</option>
          {PROPOSAL_PLATFORM_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Phase 4 — active ?batch= deep-link filter indicator. */}
      {batchParam && (
        <div className='aq-batchfilter'>
          <span className='aq-batchfilter__text'>
            {t('ShowingSingleBatch')}
          </span>
          <button
            type='button'
            className='aq-batchfilter__clear'
            onClick={clearBatchFilter}
          >
            {t('ShowAllProposals')}
          </button>
        </div>
      )}

      {/* Inline error banner — kept in-page so users can see the error after
          the global toast has auto-dismissed. */}
      {fetchError && !loading && (
        <div className='action-error' role='alert'>
          <p className='action-error__text'>{fetchError}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingActionProposals')} />
        </div>
      ) : proposals.length === 0 ? (
        // Distinguish "queue is genuinely empty" (default view, no narrowing
        // filters) from "your current filter matched nothing". The teaching
        // empty state only fits the former.
        !platformFilter &&
        (statusFilter === 'pending_review' || statusFilter === '') ? (
          <TeachingEmptyState
            icon={tickWithCircleSVG}
            title={t('ActionQueueEmptyTitle')}
            body={t('ActionQueueEmptyBody')}
          />
        ) : (
          <div className='action-empty-state'>
            <p className='action-empty-state__text'>
              {t('NoActionProposalsFound')}
            </p>
          </div>
        )
      ) : (
        <>
          <div className='aq-inbox'>
            {queueEntries.map((entry) =>
              entry.kind === 'single' ? (
                <ProposalRow
                  key={entry.proposal.id}
                  proposal={entry.proposal}
                  active={activeProposal?.id === entry.proposal.id}
                  requesterName={requesterName(entry.proposal)}
                  onOpen={openDrawer}
                  onApprove={(p) => handleApprove(p.id)}
                  onDeny={openDenyDrawer}
                  showActions={canAct && isActionable(entry.proposal.status)}
                  showStatus={statusFilter === ''}
                />
              ) : (
                <BatchCard
                  key={entry.batchId}
                  batchId={entry.batchId}
                  proposals={entry.proposals}
                  expanded={expandedBatches.has(entry.batchId)}
                  highlighted={batchParam === entry.batchId}
                  onToggle={toggleBatch}
                  canApprove={canApprove}
                  canDeny={canDeny}
                  onApproveAll={handleBatchApprove}
                  onDenyAll={handleBatchDeny}
                  activeProposalId={activeProposal?.id}
                  requesterName={requesterName}
                  onOpen={openDrawer}
                  onApprove={(p) => handleApprove(p.id)}
                  onDeny={openDenyDrawer}
                  isActionable={isActionable}
                />
              )
            )}
          </div>
          {renderPagination()}
        </>
      )}

      {/* Approve / Deny confirmation — shared Popup replaces window.confirm(). */}
      <Popup
        trigger={!!pendingConfirm}
        setTrigger={() => {
          if (!confirmSubmitting) setPendingConfirm(null);
        }}
      >
        {pendingConfirm &&
          (() => {
            const isApproveKind =
              pendingConfirm.kind === 'approve' ||
              pendingConfirm.kind === 'batchApprove';
            const isBatch =
              pendingConfirm.kind === 'batchApprove' ||
              pendingConfirm.kind === 'batchDeny';
            const title = isBatch
              ? isApproveKind
                ? t('ConfirmBatchApproveTitle')
                : t('ConfirmBatchDenyTitle')
              : isApproveKind
              ? t('ConfirmApproveActionTitle')
              : t('ConfirmDenyActionTitle');
            const message = isBatch
              ? `${
                  isApproveKind
                    ? t('ConfirmBatchApprove')
                    : t('ConfirmBatchDeny')
                } (${(pendingConfirm as { count: number }).count} ${t(
                  'BatchProposalsNoun'
                )})`
              : isApproveKind
              ? t('ConfirmApproveAction')
              : t('ConfirmDenyAction');
            // Phase 4 — "also launch on" checkboxes for creative approvals.
            // Single approve: targets = 5 platform keys minus the proposal's
            // own; thumbnail shows WHAT is being replicated (founder
            // requirement: preview visible at the decision point). Batch
            // approve: one shared row applied to every creative item.
            let replTargets: string[] = [];
            let replPreviews: CreativePreview[] = [];
            let replCreativeCount = 0;
            if (pendingConfirm.kind === 'approve') {
              const prop = pendingConfirm.proposal;
              if (prop && isCreativeProposal(prop)) {
                replTargets = replicationTargetsFor(prop);
                replPreviews = (prop.creative_previews ?? []).slice(0, 1);
              }
            } else if (pendingConfirm.kind === 'batchApprove') {
              const items = batchCreativeItems(pendingConfirm.batchId);
              if (items.length > 0) {
                replTargets = replicationTargetsForBatch(items);
                replPreviews = collectCreativePreviews(items, 3);
                replCreativeCount = items.length;
              }
            }
            return (
              <div className='action-delete-confirm'>
                <div className='action-delete-confirm__title'>
                  <span
                    className={`action-delete-confirm__title-icon${
                      isApproveKind
                        ? ' action-delete-confirm__title-icon--approve'
                        : ''
                    }`}
                    aria-hidden='true'
                  >
                    <CustomSVGs
                      svg={isApproveKind ? tickWithCircleSVG : crossSVG}
                    />
                  </span>
                  <h3>{title}</h3>
                </div>
                <p className='action-delete-confirm__message'>{message}</p>
                {/* Phase 4 — creative approvals offer cross-platform launch.
                    Default unticked; the proposal's own platform is never
                    offered. */}
                {replTargets.length > 0 && (
                  <div className='aq-replicate'>
                    {replPreviews.length > 0 && (
                      <div className='aq-replicate__thumbs' aria-hidden='true'>
                        {replPreviews.map((pv) =>
                          pv.kind === 'image' && pv.preview_url ? (
                            <img
                              key={pv.asset_id}
                              className='aq-replicate__thumb'
                              src={pv.preview_url}
                              alt=''
                              loading='lazy'
                            />
                          ) : (
                            <span
                              key={pv.asset_id}
                              className='aq-replicate__thumb aq-replicate__thumb--ph'
                            >
                              <Glyph
                                name={pv.kind === 'video' ? 'play' : 'image'}
                                size={16}
                              />
                            </span>
                          )
                        )}
                      </div>
                    )}
                    <p className='aq-replicate__label'>{t('AlsoLaunchOn')}</p>
                    <div className='aq-replicate__row'>
                      {replTargets.map((key) => (
                        <label key={key} className='aq-replicate__opt'>
                          <input
                            type='checkbox'
                            checked={replicateTargets.has(key)}
                            disabled={confirmSubmitting}
                            onChange={() => toggleReplicateTarget(key)}
                          />
                          <span>{replicationPlatformLabel(key)}</span>
                        </label>
                      ))}
                    </div>
                    {replCreativeCount > 0 && (
                      <p className='aq-replicate__note'>
                        {t('ReplicateBatchNote', { n: replCreativeCount })}
                      </p>
                    )}
                  </div>
                )}
                {/* Batch verbs accept optional notes; on deny they flow back
                    to the proposer as review feedback. */}
                {isBatch && (
                  <textarea
                    className='aq-batchnotes'
                    rows={3}
                    maxLength={2000}
                    placeholder={
                      isApproveKind
                        ? t('BatchApproveNotesPlaceholder')
                        : t('BatchDenyNotesPlaceholder')
                    }
                    value={batchNotes}
                    disabled={confirmSubmitting}
                    onChange={(e) => setBatchNotes(e.target.value)}
                  />
                )}
                <div className='action-delete-confirm__buttons'>
                  <MainSquareButton
                    type='button'
                    value={t('Cancel')}
                    buttonType='white'
                    normal={true}
                    disabled={confirmSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      setPendingConfirm(null);
                    }}
                  />
                  <MainSquareButton
                    type='button'
                    value={
                      isBatch
                        ? isApproveKind
                          ? t('ApproveAll')
                          : t('DenyAll')
                        : isApproveKind
                        ? t('Approve')
                        : t('Deny')
                    }
                    icon={isApproveKind ? tickWithCircleSVG : crossSVG}
                    buttonType={isApproveKind ? 'success' : 'danger'}
                    normal={true}
                    loading={confirmSubmitting}
                    disabled={confirmSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      executeConfirm();
                    }}
                  />
                </div>
              </div>
            );
          })()}
      </Popup>

      {/* Phase 4 — per-item results of the last batch approve/deny. */}
      <Popup trigger={!!batchResult} setTrigger={() => setBatchResult(null)}>
        {batchResult && (
          <div className='aq-batchresult'>
            <h3 className='aq-batchresult__title'>{t('BatchResultsTitle')}</h3>
            <p className='aq-batchresult__summary'>
              {[
                batchResult.response.summary.approved
                  ? `${batchResult.response.summary.approved} ${t(
                      'Approved'
                    ).toLowerCase()}`
                  : '',
                batchResult.response.summary.denied
                  ? `${batchResult.response.summary.denied} ${t(
                      'Denied'
                    ).toLowerCase()}`
                  : '',
                batchResult.response.summary.failed
                  ? `${batchResult.response.summary.failed} ${t(
                      'Failed'
                    ).toLowerCase()}`
                  : '',
                batchResult.response.summary.skipped
                  ? `${batchResult.response.summary.skipped} ${t(
                      'Skipped'
                    ).toLowerCase()}`
                  : '',
              ]
                .filter(Boolean)
                .join(' · ') || t('NoActionProposalsFound')}
            </p>
            <ul className='aq-batchresult__list'>
              {batchResult.response.results.map((r) => (
                <li
                  key={r.id}
                  className={`aq-batchresult__item aq-batchresult__item--${r.status}`}
                >
                  <div className='aq-batchresult__item-line'>
                    <span className='aq-batchresult__item-status'>
                      <Glyph
                        name={
                          r.status === 'failed' || r.status === 'skipped'
                            ? 'x'
                            : 'check'
                        }
                        size={14}
                        stroke={2.1}
                      />
                    </span>
                    <span className='aq-batchresult__item-name'>
                      {batchResultLabel(r.id)}
                    </span>
                    <span className='aq-batchresult__item-verdict'>
                      {r.status}
                      {r.reason ? ` — ${r.reason}` : ''}
                    </span>
                  </div>
                  {/* Phase 4 — per-platform replicate outcomes for this item
                      (only present when platforms were ticked on approve). */}
                  {batchResult.replication?.[r.id] && (
                    <div className='aq-batchresult__rep'>
                      {renderReplicationList(batchResult.replication[r.id])}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className='action-delete-confirm__buttons'>
              <MainSquareButton
                type='button'
                value={t('Close')}
                buttonType='light'
                normal={true}
                onClick={(e) => {
                  e.preventDefault();
                  setBatchResult(null);
                }}
              />
            </div>
          </div>
        )}
      </Popup>

      {/* Phase 4 — per-platform outcomes of a single-approve replication.
          Shown after the approve success toast; auto-created proposals appear
          in the queue via the fetch that follows the confirm. */}
      <Popup
        trigger={!!replicationResult}
        setTrigger={() => setReplicationResult(null)}
      >
        {replicationResult && (
          <div className='aq-batchresult aq-represult'>
            <h3 className='aq-batchresult__title'>
              {t('ReplicationResultsTitle')}
            </h3>
            {renderReplicationList(replicationResult)}
            <div className='action-delete-confirm__buttons'>
              <MainSquareButton
                type='button'
                value={t('Close')}
                buttonType='light'
                normal={true}
                onClick={(e) => {
                  e.preventDefault();
                  setReplicationResult(null);
                }}
              />
            </div>
          </div>
        )}
      </Popup>

      {activeProposal && (
        <ProposalDrawer
          proposal={activeProposal}
          requesterName={requesterName(activeProposal)}
          canAct={canAct && isActionable(activeProposal.status)}
          initialDenyMode={drawerDenyMode}
          submitting={drawerSubmitting}
          onClose={() => setActiveProposal(null)}
          onApprove={handleDrawerApprove}
          onDeny={handleDrawerDeny}
        />
      )}
    </SubPageInnerContentHolder>
  );
};

export default ActionQueue;
